import type { Accept } from "react-dropzone";

/**
 * MIME types for the file extensions accepted across upload surfaces. Extensions without a
 * registered type use the conventional vendor type, which matches no file during dropzone
 * checks — those files still match by extension.
 */
const extensionMimeTypes: Record<string, string> = {
  ".aac": "audio/aac",
  ".aep": "application/vnd.adobe.aftereffects.project",
  ".ai": "application/postscript",
  ".aif": "audio/aiff",
  ".aiff": "audio/aiff",
  ".alac": "audio/x-alac",
  ".avi": "video/x-msvideo",
  ".csv": "text/csv",
  ".doc": "application/msword",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".eps": "application/postscript",
  ".flac": "audio/flac",
  ".fnt": "application/x-font-fnt",
  ".gif": "image/gif",
  ".indd": "application/x-indesign",
  ".jpeg": "image/jpeg",
  ".jpg": "image/jpeg",
  ".m4a": "audio/mp4",
  ".mov": "video/quicktime",
  ".mp3": "audio/mpeg",
  ".mp4": "video/mp4",
  ".mpg": "video/mpeg",
  ".mxf": "application/mxf",
  ".otf": "font/otf",
  ".pdf": "application/pdf",
  ".png": "image/png",
  ".ppt": "application/vnd.ms-powerpoint",
  ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".psb": "image/vnd.adobe.photoshop",
  ".psd": "image/vnd.adobe.photoshop",
  ".svg": "image/svg+xml",
  ".tif": "image/tiff",
  ".tiff": "image/tiff",
  ".ttc": "font/collection",
  ".ttf": "font/ttf",
  ".wav": "audio/wav",
  ".webp": "image/webp",
  ".wma": "audio/x-ms-wma",
  ".woff": "font/woff",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".zip": "application/zip",
};

/**
 * Extensions missing from the table still need a syntactically valid MIME key, or
 * react-dropzone warns and drops them; this type matches no real file, so acceptance for
 * those entries stays extension-driven.
 */
const unmappedMimeType = "application/x-unmapped";

/**
 * Builds react-dropzone's MIME-keyed `accept` object from a flat list of file extensions
 * (`.png`) and/or MIME types (`image/*`). react-dropzone matches keys and extension values
 * independently, so grouping only affects the generated `accept` attribute, not matching.
 */
export function toDropzoneAccept(values: readonly string[]): Accept {
  const accept: Record<string, string[]> = {};

  for (const value of values) {
    if (value.includes("/")) {
      accept[value] ??= [];
      continue;
    }

    const extension = value.toLowerCase();
    (accept[extensionMimeTypes[extension] ?? unmappedMimeType] ??= []).push(extension);
  }

  return accept;
}
